import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { multiTenantApi } from '@/services/api/multi-tenant-api';
import { useAuthStore } from '@/stores/auth-store';

// ─── Public hooks (token is the requester-issued statusToken, not a JWT) ──────

export const useJoinRequestStatus = (token: string | null) =>
  useQuery({
    queryKey: ['joinRequest', 'status', token],
    queryFn: () => multiTenantApi.getJoinRequestStatus(token!),
    enabled: !!token,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

export const useResendReminder = () =>
  useMutation({
    mutationFn: (token: string) => multiTenantApi.resendJoinRequestReminder(token),
  });

// ─── Admin hooks (require a JWT from the auth store) ──────────────────────────

export const useAdminPendingRequests = (tenantId: string | undefined) => {
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  return useQuery({
    queryKey: ['admin', 'joinRequests', tenantId],
    queryFn: () => multiTenantApi.listPendingJoinRequests(tenantId!, accessToken!),
    enabled: !!tenantId && !!accessToken,
    refetchOnWindowFocus: true,
  });
};

export const useAdminPendingCount = (tenantId: string | undefined) => {
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  return useQuery({
    queryKey: ['admin', 'joinRequestsCount', tenantId],
    queryFn: () =>
      multiTenantApi.getPendingJoinRequestCount(tenantId!, accessToken!).then((r) => r.pending),
    enabled: !!tenantId && !!accessToken,
    refetchInterval: 60_000,
  });
};

export const useApproveJoinRequest = (tenantId: string | undefined) => {
  const qc = useQueryClient();
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  return useMutation({
    mutationFn: (id: string) => multiTenantApi.approveJoinRequest(id, accessToken!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'joinRequests', tenantId] });
      qc.invalidateQueries({ queryKey: ['admin', 'joinRequestsCount', tenantId] });
    },
  });
};

export const useRejectJoinRequest = (tenantId: string | undefined) => {
  const qc = useQueryClient();
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      multiTenantApi.rejectJoinRequest(id, reason, accessToken!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'joinRequests', tenantId] });
      qc.invalidateQueries({ queryKey: ['admin', 'joinRequestsCount', tenantId] });
    },
  });
};

export const useBulkApproveJoinRequests = (tenantId: string | undefined) => {
  const qc = useQueryClient();
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  return useMutation({
    mutationFn: (ids: string[]) => multiTenantApi.bulkApproveJoinRequests(ids, accessToken!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'joinRequests', tenantId] });
      qc.invalidateQueries({ queryKey: ['admin', 'joinRequestsCount', tenantId] });
    },
  });
};

export const useBulkRejectJoinRequests = (tenantId: string | undefined) => {
  const qc = useQueryClient();
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  return useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: string }) =>
      multiTenantApi.bulkRejectJoinRequests(ids, reason, accessToken!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'joinRequests', tenantId] });
      qc.invalidateQueries({ queryKey: ['admin', 'joinRequestsCount', tenantId] });
    },
  });
};
